import React, { forwardRef, ForwardedRef } from "react";

import { TextAreaRef } from "antd/es/input/TextArea";

import { useFormAtom } from "../../../utils/form/formAtom/hooks";
import {
    FormAtom,
    FormValidationError,
} from "../../../utils/form/formAtom/types";
import {
    extractRequired,
    extractStringMaxLength,
    extractStringMinLength,
} from "../../../utils/form/formAtom/validation";
import { assertNever } from "../../../utils/obj";
import {
    StringInput as BaseStringInput,
    StringInputBaseProps,
    StringInputProps as BaseStringInputProps,
} from "../fields/stringInput";

export interface FAStringInputProps extends StringInputBaseProps {
    atom: FormAtom<string>;
    // onBlur (default): Automatically dispatches on blur (and so after the blur caused by onPressEnter)
    // onPressEnter: Restrict automatic dispatches to onPressEnter events. You may want to manually dispatch, like on a button press, for better UX
    dispatch?: "onBlur" | "onPressEnter";
    parentError?: FormValidationError | undefined;
    onKeyDown?: React.KeyboardEventHandler;
}

/**
 * Basic form component based on ant design's Input
 *
 * This component leverages the following constraints from the validator:
 *  - `min(n)` is mapped to `minLength={n}`
 *  - `max(n)` is mapped to `maxLength={n}`
 *  - `required` is mapped to `required`
 */
export const FAStringInput = forwardRef(
    (
        {
            atom,
            label,
            placeholder,
            maxRows,
            parentError,
            onKeyDown,
            disabled = false,
            styleVariant = "normal",
            autoFocus = false,
            dispatch = "onBlur",
        }: FAStringInputProps,
        forwardedRef: ForwardedRef<TextAreaRef>
    ) => {
        const { value, error, setCacheValue, dispatchValue, schema } =
            useFormAtom(atom);

        let onBlur: BaseStringInputProps["onBlur"];
        let onPressEnter: BaseStringInputProps["onPressEnter"];
        switch (dispatch) {
            case "onBlur":
                onBlur = (event) => {
                    if (event !== undefined) {
                        dispatchValue(event.target.value);
                    }
                };
                onPressEnter = () => {};
                break;
            case "onPressEnter":
                onBlur = () => {
                    // Don't dispatch the new value if we're in onPressEnter mode
                };
                onPressEnter = () => {
                    dispatchValue(value);
                };
                break;
            default:
                return assertNever(dispatch);
        }

        return (
            <BaseStringInput
                label={label}
                value={value}
                onChange={(value) => {
                    setCacheValue(value);
                }}
                onBlur={onBlur}
                onPressEnter={onPressEnter}
                onKeyDown={onKeyDown}
                minLength={extractStringMinLength(schema)}
                maxLength={extractStringMaxLength(schema)}
                required={extractRequired(schema)}
                error={error?.value ?? parentError?.value}
                placeholder={placeholder}
                maxRows={maxRows}
                disabled={disabled}
                styleVariant={styleVariant}
                autoFocus={autoFocus}
                ref={forwardedRef}
            />
        );
    }
);
